import { useState, useRef, useEffect, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { ChatHeader } from "./ChatHeader";
import { ChatHistory } from "./ChatHistory";
import { ChatInput } from "./ChatInput";
import { MessagesList } from "./MessagesList";
import { chatApi, Conversation, ConversationStatus, type ChatMessage } from "@/api";
import { withTypingDelay, forceDelay } from "@/lib/latency";
import { ChatContainerProps } from "@/types/componentsTypes";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useAttachConversationToUser, useGetUserActiveConversation } from "@/hooks/useAuth";
import { useLanguage } from "@/contexts/LanguageContext";
import { useGetConversationById } from "@/hooks/useConversation";
import { ClosedConversationLabel } from "./ClosedConversationLabel";

function makeInitialMessages(t: (key: string) => string): ChatMessage[] {
  return [
    {
      id: "1",
      content: t("initialWelcome"),
      isUser: false,
      timestamp: new Date(),
      options: [
        {
          id: "known-destination",
          title: t("optionKnownDestinationTitle"),
          description: t("optionKnownDestinationDesc"),
          image:
            "https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=150&h=100&fit=crop&crop=center",
        },
        {
          id: "partial-requirements",
          title: t("optionPartialReqTitle"),
          description: t("optionPartialReqDesc"),
          image:
            "https://images.unsplash.com/photo-1469474968028-56623f02e42e?w=150&h=100&fit=crop&crop=center",
        },
      ],
    },
  ];
}

export function ChatContainer({ onClose }: ChatContainerProps) {
  const { language, t } = useLanguage();
  // Keep a ref so callbacks/effects always have the latest t without needing it in deps
  const tRef = useRef(t);
  tRef.current = t;

  const [messages, setMessages] = useState<ChatMessage[]>(() => makeInitialMessages(t));
  const [isTyping, setIsTyping] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [isConversationClosed, setIsConversationClosed] = useState(false);
  const [hasActiveConversation, setHasActiveConversation] = useState(false);
  const activeConversation = useGetUserActiveConversation();
  const attachConversationToUser = useAttachConversationToUser();
  const initialActiveConversationLoadUserRef = useRef<string | null>(null);

  const { user } = useCurrentUser();

  const loadActiveConversation = useCallback(
    (
      userId: string,
      options?: Parameters<typeof activeConversation.mutate>[1]
    ) => {
      activeConversation.mutate(userId, options);
    },
    [activeConversation.mutate]
  );

  const requestInitialActiveConversation = useCallback(
    (
      userId: string,
      options?: Parameters<typeof activeConversation.mutate>[1]
    ) => {
      initialActiveConversationLoadUserRef.current = userId;
      loadActiveConversation(userId, options);
    },
    [loadActiveConversation]
  );

  // When language changes, refresh initial messages if user hasn't started chatting yet
  useEffect(() => {
    setMessages(prev => {
      if (prev.length === 1 && prev[0].id === "1") {
        return makeInitialMessages(tRef.current);
      }
      return prev;
    });
  }, [language]);

  // Helper to create state from a fetched conversation
  const createFromConversation = (conv: Conversation) => {
    if (!conv) return;
    const mapped = conv.messages.map((m: any) => ({
      id: m.id,
      content: typeof m.message === 'string' ? m.message : String(m.message),
      isUser: m.sender === 'user',
      timestamp: new Date(m.timestamp),
      options: m.options || [],
    }));
    if (mapped.length) {
      setMessages(mapped);
      setConversationId(conv.id);
      setIsConversationClosed(conv.status === ConversationStatus.CLOSED);
      setHasActiveConversation(true);
    }
  };

  // Track previous auth state to detect logout transitions
  const prevUserRef = useRef<typeof user>(user);
  useEffect(() => {
    // User logged in after being a guest
    if (!prevUserRef.current && user) {
      if (conversationId) {
        // Guest conversation exists: attach it to user
        attachConversationToUser.mutate(
          { conversationId, userId: user.id },
          {
            onError: () => {
              // If attach fails, fall back to trying active conversation
              requestInitialActiveConversation(user.id, {
                onSuccess: createFromConversation,
              });
            }
          }
        );
      } else {
        // No guest conversation: load latest active from server
        requestInitialActiveConversation(user.id, {
          onSuccess: createFromConversation,
        });
      }
    }

    // User logged out -> reset socket and conversation state
    if (prevUserRef.current && !user) {
      if (typeof (chatApi as any).reset === 'function') {
        (chatApi as any).reset();
      }
      initialActiveConversationLoadUserRef.current = null;
      setMessages(makeInitialMessages(tRef.current));
      setConversationId(null);
      setShowHistory(false);
    }
    prevUserRef.current = user;
  }, [user, conversationId, attachConversationToUser, requestInitialActiveConversation]);

  // On initial mount (or hydration) when authed user present, load latest conversation
  useEffect(() => {
    if (
      user?.id &&
      !conversationId &&
      messages.length === 1 &&
      messages[0].id === "1" &&
      initialActiveConversationLoadUserRef.current !== user.id
    ) {
      requestInitialActiveConversation(user.id, {
        onSuccess: createFromConversation,
        onError: () => {
          // Token expired or auth error - reset state
          chatApi.reset();
          setConversationId(null);
        }
      });
    }
  }, [user?.id, conversationId, messages, requestInitialActiveConversation]);

  const getConversationById = useGetConversationById();

  // Scroll refs to avoid deps on `messages` (callbacks)
  const listRef = useRef<HTMLDivElement>(null);
  const messagesRef = useRef(messages);
  const prevMessagesCountRef = useRef(messages.length);
  const hasInitialScrolledRef = useRef(false);
  
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  // Scroll down on native container
  const scrollToBottom = useCallback((behavior: ScrollBehavior = "smooth") => {
    const el = listRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior });
  }, []);

  // Scroll to bottom on initial load
  useEffect(() => {
    if (!hasInitialScrolledRef.current && messages.length > 0) {
      setTimeout(() => {
        scrollToBottom("smooth");
        hasInitialScrolledRef.current = true;
      }, 200);
    }
  }, [messages.length, scrollToBottom]);

  useEffect(() => {
    const el = listRef.current;
    if (!el || !hasInitialScrolledRef.current) return;

    // Check if a new message was added
    if (messages.length > prevMessagesCountRef.current) {
      const firstNewMessageIndex = prevMessagesCountRef.current;
      const newMessages = messages.slice(firstNewMessageIndex);
      
      // Find first agent message in the newly added messages
      const firstAgentMessage = newMessages.find(m => !m.isUser);
      
      // For user messages, scroll to bottom
      if (newMessages.some(m => m.isUser) && !firstAgentMessage) {
        prevMessagesCountRef.current = messages.length;
        requestAnimationFrame(() => scrollToBottom("smooth"));
      } 
      // For agent messages, scroll to beginning of first agent message
      else if (firstAgentMessage) {
        prevMessagesCountRef.current = messages.length;
        // Use double rAF to ensure layout is complete
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const firstAgentMessageEl = el.querySelector(`[data-message-id="${firstAgentMessage.id}"]`);
            
            if (firstAgentMessageEl) {
              // Manually calculate offset within the scroll container
              const containerRect = el.getBoundingClientRect();
              const messageRect = firstAgentMessageEl.getBoundingClientRect();
              const scrollTarget = messageRect.top - containerRect.top + el.scrollTop;
              el.scrollTo({ top: scrollTarget, behavior: 'smooth' });
            }
          });
        });
      }
    } else if (isTyping) {
      // Only scroll to bottom for typing indicator when actively typing
      requestAnimationFrame(() => scrollToBottom("smooth"));
    }
  }, [messages, isTyping, scrollToBottom]);

  // Auto-scroll on tab changes
  useEffect(() => {
    if (!showHistory) {
      requestAnimationFrame(() => scrollToBottom("auto"));
    }
  }, [showHistory, scrollToBottom]);

  const handleSendMessage = useCallback(
    async (content: string) => {
      const userMessage: ChatMessage = {
        id: Date.now().toString(),
        content,
        isUser: true,
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, userMessage]);
      setIsTyping(true);

      try {
        const response = await withTypingDelay(
          chatApi.sendMessage({
            message: content,
            // If user logged out, force a new conversation by omitting id
            conversationId: user ? (conversationId || undefined) : undefined,
            context: { previousMessages: messagesRef.current },
          }),
          content
        );

        if (!conversationId && response.conversationId) {
          setConversationId(response.conversationId);
        }
        // Ensure all response messages have IDs
        const messagesWithIds = response.messages.map((msg) => ({
          ...msg,
          id: msg.id || Date.now().toString() + Math.random().toString(36).substr(2, 9)
        }));
        setIsTyping(false);
        setMessages((prev) => [...prev, ...messagesWithIds]);

        setIsConversationClosed(response.status === ConversationStatus.CLOSED);
        (response.status === ConversationStatus.CLOSED) && setHasActiveConversation(false);
      } catch {
        await forceDelay(400);
        setIsTyping(false);
        setMessages((prev) => [
          ...prev,
          {
            id: (Date.now() + 1).toString(),
            content: tRef.current("connectErrorSend"),
            isUser: false,
            timestamp: new Date(),
          },
        ]);
      }
    },
    [conversationId]
  );

  const handleOptionSelect = useCallback(
    async (optionId: string, optionTitle: string) => {
      const userMessage: ChatMessage = {
        id: Date.now().toString(),
        content: optionTitle,
        isUser: true,
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, userMessage]);
      setIsTyping(true);

      try {
        const response = await withTypingDelay(
          chatApi.sendMessage({
            message: optionTitle,
            conversationId: user ? (conversationId || undefined) : undefined,
            context: {
              selectedOptionId: optionId,
              previousMessages: messagesRef.current,
            },
          }),
          optionTitle
        );

        if (!conversationId && response.conversationId) {
          setConversationId(response.conversationId);
        }
        // Ensure all response messages have IDs
        const messagesWithIds = response.messages.map((msg) => ({
          ...msg,
          id: msg.id || Date.now().toString() + Math.random().toString(36).substr(2, 9)
        }));
        setIsTyping(false);
        setMessages((prev) => [...prev, ...messagesWithIds]);
      } catch {
        await forceDelay(400);
        setIsTyping(false);
        setMessages((prev) => [
          ...prev,
          {
            id: (Date.now() + 1).toString(),
            content: tRef.current("connectErrorOption"),
            isUser: false,
            timestamp: new Date(),
          },
        ]);
      }
    },
    [conversationId]
  );

  const handleSelectConversation = async (convId: string) => {
    // Reset WS connection to bind to selected conversation
    chatApi.switchConversation(convId);

    const conversation: Conversation = await getConversationById.mutateAsync(convId);
    const chatMessages: ChatMessage[] = conversation.messages.map(
      (m) => ({
        id: m.id,
        content: m.message,
        isUser: m.sender === "user",
        timestamp: new Date(m.timestamp),
        options: m.options || [],
      })
    );

    setMessages(chatMessages);
    setConversationId(convId);
    setShowHistory(false);
    setIsConversationClosed(conversation.status === ConversationStatus.CLOSED);
  };

  const handleStartNewChat = useCallback(() => {
    // Reset websocket connection
    chatApi.reset();
    
    // Reset conversation state
    setMessages(makeInitialMessages(tRef.current));
    setConversationId(null);
    setIsConversationClosed(false);
    setIsTyping(false);
  }, [language]);

  const handleBackToActiveChat = useCallback(() => {
    if (user?.id) {
      loadActiveConversation(user.id, {
        onSuccess: (conv) => {
          if (conv) {
            handleSelectConversation(conv.id);
          }
        }
      });
    }
  }, [user?.id, loadActiveConversation]);

  if (showHistory) {
    return (
      <Card className="flex flex-col h-full shadow-xl border border-gray-200 rounded-2xl overflow-hidden">
        <ChatHistory
          onClose={() => setShowHistory(false)}
          onSelectConversation={handleSelectConversation}
        />
      </Card>
    );
  }

  return (
    <Card className="flex flex-col h-full shadow-xl border border-gray-200 rounded-2xl overflow-hidden">
      <ChatHeader onClose={onClose} />

      {/* min-h-0 ca lista să fie scrollabilă în flex */}
      <div className="flex-1 min-h-0 bg-white">
        <MessagesList
          ref={listRef}
          messages={messages}
          isTyping={isTyping}
          onOptionSelect={handleOptionSelect}
          isConversationClosed={isConversationClosed}
        />
      </div>

      {isConversationClosed ? (
        <ClosedConversationLabel 
          onStartNewChat={handleStartNewChat}
          onBackToActiveChat={hasActiveConversation ? handleBackToActiveChat : undefined}
          hasActiveConversation={hasActiveConversation}
        />
      ) : (
        <ChatInput onSendMessage={handleSendMessage} disabled={isTyping} />
      )}

      {user && (
        <div
          className="p-3 border-t cursor-pointer bg-gray-100 hover:bg-gray-200 flex items-center gap-2 text-sm font-semibold text-gray-700"
          onClick={() => setShowHistory(true)}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 text-gray-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          {t("conversationsHistory")}
        </div>
      )}
    </Card>
  );
}
